import React from "react"
import AllowEmptySettingItem from "./AllowEmptySettingItem"
import HierarchySettingsPay from "./HierarchySettingsPay"
import HierarchySettingsOTPolicy from "./HierarchySettingsOTPolicy"
import HierarchySettingsAutoBreaks from "./HierarchySettingsAutoBreaks"
import HierarchySettingsMobileBreaks from "./HierarchySettingsMobileBreaks"
import HierarchySettingsAutoTransfer from "./HierarchySettingsAutoTransfer"
import HierarchySettingsOvertime from "./HierarchySettingsOvertime"
import HierarchySettingsPosition from "./HierarchySettingsPosition"
import HierarchySettingsRoundingRules from "./HierarchySettingsRoundingRules"
import HierarchySettingsPunchRules from "./HierarchySettingsPunchRules"
import HierarchySettingsApprovedIPAddress from "./HierarchySettingsApprovedIPAddress"
import HierarchySettingsPunchQuestions from "./HierarchySettingsPunchQuestions"
import HierarchySettingsScheduleBudgetUnits from "./HierarchySettingsScheduleBudgetUnits"
import AccountStore from "../../stores/AccountStore"
import { IHierarchyItem, IHierarchyItemForAccountLevel, IJobDocument } from "../../models"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import strings from "../../localization/strings"
import _ from "lodash"
import HierarchySettingsApprovedIVRNumbers from "./HierarchySettingsApprovedIVRNumbers"
import AuthenticationStore from "../../stores/AuthenticationStore"

interface IProps {
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    readonly: boolean
    handleUpdateJob?(job: IJobDocument): Promise<void>
}

const HierarchySettings: React.SFC<IProps> = (props: IProps) => {
    const hierarchyJobItem = _.cloneDeep(props.hierarchyJobItem)
    delete (hierarchyJobItem as IJobDocument).TicksSinceChange
    delete (hierarchyJobItem as IJobDocument).ObjectAudit
    const inheritedHierarchySettingItems = hierarchyHelper.getInheritedHierarchySettingItems(hierarchyJobItem)
    const isJobSettings = hierarchyJobItem.hasOwnProperty("AccountId")
    return (
        <div>
            <div style={{ marginBottom: 30 }}>
                <HierarchySettingsPay
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.PayGroupSettings as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly || (isJobSettings && !AccountStore.getEnablePayGroupAssignEmployeeLevel())}
                    isJobSettings={isJobSettings}
                    handleUpdateJob={props.handleUpdateJob}
                />
            </div>
            {AccountStore.getEnableOTPolicy() &&
                <div style={{ marginBottom: 30 }}>
                    <HierarchySettingsOTPolicy
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.OTPolicySettings as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        isJobSettings={isJobSettings}
                        handleUpdateJob={props.handleUpdateJob}
                    />
                </div>
            }
            <div style={{ marginBottom: 30 }}>
                <AllowEmptySettingItem
                    cardTitle={strings.RoundingRules}
                    useToggleLbl={strings.Use_Rounding_Rules}
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.RoundingRulesSettings as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly}
                    settingObjPropertyName="RoundingRulesSettings"
                    handleUpdateJob={props.handleUpdateJob}
                >
                    <HierarchySettingsRoundingRules />
                </AllowEmptySettingItem>
            </div>
            <div style={{ marginBottom: 30 }}>
                <AllowEmptySettingItem
                    cardTitle={strings.AutoBreaks}
                    useToggleLbl={strings.Use_Auto_Breaks}
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.AutoBreakSettings as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly}
                    settingObjPropertyName="AutoBreakSettings"
                    handleUpdateJob={props.handleUpdateJob}
                >
                    <HierarchySettingsAutoBreaks />
                </AllowEmptySettingItem>
            </div>
            <div style={{ marginBottom: 30 }}>
                <HierarchySettingsMobileBreaks
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.MobileBreaks as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly}
                    handleUpdateJob={props.handleUpdateJob}
                />
            </div>
            <div style={{ marginBottom: 30 }}>
                <HierarchySettingsAutoTransfer
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.AutoTransferSettings as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly}
                    handleUpdateJob={props.handleUpdateJob}
                />
            </div>
            <div style={{ marginBottom: 30 }}>
                <HierarchySettingsPosition
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.PositionSelectionSettings as (IHierarchyItem | IJobDocument)}
                    readonly
                    handleUpdateJob={props.handleUpdateJob}
                />
            </div>
            <div style={{ marginBottom: 30 }}>
                <HierarchySettingsOvertime
                    hierarchyJobItem={hierarchyJobItem}
                    inheritedHierarchyJobItem={inheritedHierarchySettingItems.CaliforniaOvertimeRule as (IHierarchyItem | IJobDocument)}
                    readonly={props.readonly || AccountStore.getHideOT2()}
                    handleUpdateJob={props.handleUpdateJob}
                />
            </div>
            {AccountStore.getPunchRuleEnabled() &&
                <div style={{ marginBottom: 30 }}>
                    <AllowEmptySettingItem
                        cardTitle={strings.Punch_Rules}
                        useToggleLbl={strings.Use_Punch_Rules}
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.PunchRulesSettings as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        settingObjPropertyName="PunchRulesSettings"
                        handleUpdateJob={props.handleUpdateJob}
                        permissionKey = {AuthenticationStore.Permissions.Timecards.PunchRuleOverride}
                    >
                        <HierarchySettingsPunchRules />
                    </AllowEmptySettingItem>
                </div>
            }
            {AccountStore.getPunchQuestionEnabled() &&
                <div style={{ marginBottom: 30 }}>
                    <AllowEmptySettingItem
                        cardTitle={strings.Punch_Questions}
                        useToggleLbl={strings.Use_Punch_Question}
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.PunchQuestion as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        settingObjPropertyName="PunchQuestion"
                        handleUpdateJob={props.handleUpdateJob}
                    >
                        <HierarchySettingsPunchQuestions />
                    </AllowEmptySettingItem>
                </div>
            }
            {!isJobSettings &&
                <div style={{ marginBottom: 30 }}>
                    <HierarchySettingsApprovedIPAddress
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.ApprovedIPAddresses as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        handleUpdateJob={props.handleUpdateJob}
                    />
                </div>
            }
            {isJobSettings && hierarchyJobItem && AccountStore.getIVREnabled() &&
                <div style={{ marginBottom: 30 }}>
                    <HierarchySettingsApprovedIVRNumbers
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.ApprovedIVRPhoneNumbers as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        handleUpdateJob={props.handleUpdateJob}
                    />
                </div>
            }
            {AccountStore.getEnableDailyScheduleBudget() &&
                <div style={{ marginBottom: 30 }}>
                    <AllowEmptySettingItem
                        cardTitle={strings.Schedule_Budget_Units}
                        useToggleLbl={strings.Use_Schedule_Budget_Units}
                        hierarchyJobItem={hierarchyJobItem}
                        inheritedHierarchyJobItem={inheritedHierarchySettingItems.ScheduleBudgetUnitsSettings as (IHierarchyItem | IJobDocument)}
                        readonly={props.readonly}
                        settingObjPropertyName="ScheduleBudgetUnitsSettings"
                        handleUpdateJob={props.handleUpdateJob}
                    >
                        <HierarchySettingsScheduleBudgetUnits />
                    </AllowEmptySettingItem>
                </div>
            }
        </div>
    )
}

export default HierarchySettings
